/**
 * Der **Ausschnitt** am Kopf einer Stelle: die Zeilen, von denen ihre Befunde
 * sprechen, samt ein paar Zeilen Zusammenhang (Glossar, „Ausschnitt").
 *
 * Er beantwortet die Frage, die eine Stelle bisher offenließ. „Rechnung.php @
 * r4711 · Zeilen 42–44" nennt einen Ort; was dort steht, stand weiter unten in
 * einer eigenen Fläche, die erst aufzuschlagen war — und wer drei Stellen
 * hintereinander liest, schlägt dreimal auf. Der Ausschnitt steht deshalb **vor** den
 * Befunden der Stelle: Erst der Code, dann was man darüber gesagt hat.
 *
 * Drei Festlegungen tragen ihn:
 *
 * - **Nur zu lesen.** Markiert wird im Code, und die Stelle ist hier schon
 *   markiert; ein zweiter Markierweg an derselben Sache ergäbe zwei Anker für
 *   einen Befund. Der Weg in den Code steht daneben als Knopf.
 * - **Die Zeilen der Stelle sind hervorgehoben**, und zwar mit derselben Marke wie
 *   im Diff — dieselbe Sache, dasselbe Zeichen. Der Zusammenhang darüber und
 *   darunter steht ungefärbt da.
 * - **Was fehlt, sagt er.** Zu einer übersprungenen Dateiänderung gibt es keinen
 *   Text; dann steht der Grund da und keine leere Fläche. Ein Ausschnitt aus dem Diff
 *   sagt ebenfalls, was er ist: Dort fehlt, was zwischen zwei Abschnitten liegt.
 *
 * Gerechnet wird das alles im Server (`Revision\Excerpt`): Welcher Stand die
 * Zeilen zählt, folgt aus dem Anker (ADR-0006), und diese Rechnung gibt es genau
 * einmal.
 */

import { useMemo } from "react";
import type { ReactElement } from "react";

import { Icon } from "../../design-system";
import type { DiffLine, SiteExcerpt } from "../../api/client";
import { Notice } from "../loading";
import { CodeLine, CodeLines, labelOfDiffLine } from "./code";
import { highlight, languageOf } from "./highlight";

/**
 * Was dasteht, wenn zu den Zeilen kein Text im Bestand liegt — eine
 * übersprungene Dateiänderung, eine Binärdatei, ein Diff ohne Abschnitt.
 */
const NO_TEXT =
  "Zu diesen Zeilen liegt kein Text im Bestand — die Dateiänderung ist " +
  "übersprungen oder trägt keinen Diff.";

/** Und was dasteht, wenn der Ausschnitt aus dem Diff kommt statt aus der Datei. */
const FROM_DIFF =
  "Aus dem Diff — für diesen Stand ist keine ganze Datei abgelegt. Was zwischen " +
  "zwei Abschnitten liegt, fehlt hier.";

export function Excerpt({ excerpt }: { excerpt: SiteExcerpt }): ReactElement {
  // Ein Strom über die gelieferten Zeilen, und einer je Ausschnitt: Was zwischen
  // zwei Ausschnitten liegt, gehört nicht zu diesem hier.
  const tinted = useMemo(
    () => highlight(excerpt.lines.map((line) => line.text), languageOf(excerpt.path)),
    [excerpt],
  );

  // Eine Abwesenheit ist eine Meldung und keine Fußnote: Sie steht in derselben
  // Form da wie jede andere Auskunft dieser Anwendung darüber, dass etwas fehlt.
  if (excerpt.lines.length === 0) {
    return <Notice text={NO_TEXT} glyph="triangle-alert" />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <div
        role="group"
        aria-label="Ausschnitt"
        className="erp-scroll"
        style={{
          // Der Ausschnitt ist ein Zitat und keine Fläche: eingesenkt, gerahmt, und
          // waagerecht rollbar statt umbrechend — eine umgebrochene Codezeile
          // wäre eine Zeile, die es in der Datei nicht gibt.
          overflowX: "auto",
          padding: "var(--sp-3) 0",
          background: "var(--bg-sunken)",
          border: "1px solid var(--line-f)",
          borderRadius: "var(--r-field)",
        }}
      >
        {/*
          Dieselbe Fläche wie im Diff — der Ausschnitt steht unmittelbar darüber, wo
          zwei verschiedene Zeilenbreiten sofort auffielen. Der Rollbereich ist
          derselbe wie bisher: dieser Rahmen hier.
        */}
        <CodeLines>
          {excerpt.lines.map((line, index) => (
            <CodeLine
              key={index}
              label={labelOfDiffLine(line, numberOf(line, excerpt.side))}
              gutter={[line.oldLine, line.newLine]}
              sign={line.kind === "added" ? "+" : line.kind === "removed" ? "-" : " "}
              tokens={tinted[index] ?? []}
              tint={line.kind}
              marked={anchored(line, excerpt)}
            />
          ))}
        </CodeLines>
      </div>

      {excerpt.truncated ? (
        <Hint
          text={`Nur der Anfang: Die Stelle reicht bis Zeile ${String(excerpt.endLine)}.`}
          glyph="chevrons-down"
        />
      ) : null}

      {excerpt.source === "diff" ? <Hint text={FROM_DIFF} glyph="info" /> : null}
    </div>
  );
}

/**
 * Liegt diese Zeile im Anker der Stelle?
 *
 * Gefragt wird auf der Seite, die der Ausschnitt nennt: Eine entfernte Zeile 42 und
 * eine hinzugefügte Zeile 42 sind zwei verschiedene Zeilen, und nur eine von
 * beiden gehört zur Stelle.
 */
function anchored(line: DiffLine, excerpt: SiteExcerpt): boolean {
  const number = numberOf(line, excerpt.side);

  return number !== null && number >= excerpt.startLine && number <= excerpt.endLine;
}

function numberOf(line: DiffLine, side: "old" | "new"): number | null {
  return side === "old" ? line.oldLine : line.newLine;
}

/** Ein Satz über dem oder unter dem Ausschnitt — was er ist, oder was ihm fehlt. */
function Hint({ text, glyph }: { text: string; glyph: string }): ReactElement {
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-3)",
        fontSize: "var(--fs-11)",
        color: "var(--fg-6)",
        textWrap: "pretty",
      }}
    >
      <Icon name={glyph} size={13} />
      {text}
    </span>
  );
}
